/** @module @liquidefy/code-editor/ai/ai-response @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  AIResponse,
  AIResponseStatus,
  AIContent,
  AIModelInfo,
  AIPerformanceInfo,
  AIRequest,
  AIRequestId,
  AIResponseId,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asAIResponseId,
  now,
} from '../../code-editor.js';

// ═══ §2 RESPONSE CONSTRUCTION — build AIResponse objects ═══

/**
 * Create a full AIResponse with all fields.
 */
export function createResponse(
  requestId: AIRequestId,
  status: AIResponseStatus,
  content: AIContent,
  model: AIModelInfo,
  performance: AIPerformanceInfo,
  confidence: number,
): AIResponse {
  return {
    id: asAIResponseId(generateId('aires')),
    requestId,
    status,
    content,
    model,
    performance,
    confidence,
  };
}

/**
 * Create a success response.
 */
export function createSuccessResponse(
  requestId: AIRequestId,
  content: AIContent,
  model: AIModelInfo,
  performance: AIPerformanceInfo,
  confidence: number,
): AIResponse {
  return createResponse(requestId, 'success', content, model, performance, confidence);
}

/**
 * Create an error response.
 */
export function createErrorResponse(
  requestId: AIRequestId,
  error: unknown,
  model: AIModelInfo,
): AIResponse {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return createResponse(
    requestId,
    'error',
    { type: 'text', value: errorMessage },
    model,
    { totalTimeMs: 0, inferenceTimeMs: 0, tokensProcessed: 0, tokensGenerated: 0, cached: false },
    0,
  );
}

/**
 * Create a partial response (still in progress).
 */
export function createPartialResponse(
  requestId: AIRequestId,
  content: AIContent,
  model: AIModelInfo,
  performance: AIPerformanceInfo,
): AIResponse {
  return createResponse(requestId, 'partial', content, model, performance, 0.5);
}

/**
 * Create a timeout response.
 */
export function createTimeoutResponse(
  requestId: AIRequestId,
  model: AIModelInfo,
  elapsedMs: number,
): AIResponse {
  return createResponse(
    requestId,
    'timeout',
    { type: 'text', value: `Request timed out after ${elapsedMs}ms` },
    model,
    { totalTimeMs: elapsedMs, inferenceTimeMs: elapsedMs, tokensProcessed: 0, tokensGenerated: 0, cached: false },
    0,
  );
}

// ═══ §3 CONTENT EXTRACTION — pull typed data from responses ═══

/**
 * Extract raw text from a response, returns empty string if not text content.
 */
export function extractText(response: AIResponse): string {
  if (response.content.type === 'text') return String(response.content.value ?? '');
  if (response.content.type === 'conversation') return String(response.content.value ?? '');
  return contentToString(response.content);
}

/**
 * Extract code from a response.
 */
export function extractCode(response: AIResponse): string {
  if (response.content.type === 'code') return String(response.content.value ?? '');
  // Try to extract code blocks from text content
  const text = String(response.content.value ?? '');
  const codeBlockMatch = text.match(/```[\s\S]*?\n([\s\S]*?)```/);
  if (codeBlockMatch) return codeBlockMatch[1]!.trim();
  return '';
}

/**
 * Extract suggestions array from a response.
 */
export function extractSuggestions(response: AIResponse): string[] {
  if (response.content.type === 'suggestions') {
    const val = response.content.value;
    if (Array.isArray(val)) return val.map(String);
  }
  return [];
}

/**
 * Extract analysis data from a response.
 */
export function extractAnalysis(response: AIResponse): Record<string, unknown> | null {
  if (response.content.type === 'analysis') {
    const val = response.content.value;
    if (val && typeof val === 'object') return val as Record<string, unknown>;
  }
  return null;
}

/**
 * Extract structured data from a response.
 */
export function extractStructured(response: AIResponse): unknown {
  if (response.content.type === 'structured') return response.content.value;
  return null;
}

/**
 * Convert any AIContent to a string representation.
 */
export function contentToString(content: AIContent): string {
  if (content.value === null || content.value === undefined) return '';
  if (typeof content.value === 'string') return content.value;
  if (Array.isArray(content.value)) return content.value.map(String).join('\n');
  if (typeof content.value === 'object') {
    try {
      return JSON.stringify(content.value, null, 2);
    } catch {
      return '[Object]';
    }
  }
  return String(content.value);
}

/**
 * Return the content type label.
 */
export function contentType(content: AIContent): AIContent['type'] {
  return content.type;
}

/**
 * Check whether the response contains code content.
 */
export function hasCodeContent(response: AIResponse): boolean {
  if (response.content.type === 'code') return true;
  const text = String(response.content.value ?? '');
  return text.includes('```');
}

/**
 * Check whether the response contains text content.
 */
export function hasTextContent(response: AIResponse): boolean {
  return response.content.type === 'text' || response.content.type === 'conversation';
}

// ═══ §4 QUALITY ASSESSMENT — evaluate response quality ═══

/**
 * Quality threshold constants.
 */
export const QUALITY_THRESHOLDS = {
  low: 0.3,
  medium: 0.5,
  high: 0.7,
  veryHigh: 0.9,
} as const;

/**
 * Assess the overall quality of a response relative to the original request.
 * Returns a score 0-1.
 */
export function assessResponseQuality(response: AIResponse, request: AIRequest): number {
  if (response.status === 'error' || response.status === 'timeout') return 0;
  if (response.status === 'cancelled') return 0;

  const factors = qualityFactors(response);
  const relevanceWeight = 0.35;
  const completenessWeight = 0.3;
  const accuracyWeight = 0.2;
  const coherenceWeight = 0.15;

  let score =
    factors.relevance * relevanceWeight +
    factors.completeness * completenessWeight +
    factors.accuracy * accuracyWeight +
    factors.coherence * coherenceWeight;

  // Boost if confidence is high
  if (response.confidence > 0.8) score = Math.min(1, score * 1.1);

  // Penalize if the response quality is below the request threshold
  if (score < request.qualityThreshold * 0.5) score *= 0.7;

  return Math.max(0, Math.min(1, score));
}

/**
 * Check if a response meets a quality threshold.
 */
export function isHighQuality(response: AIResponse, threshold: number = QUALITY_THRESHOLDS.high): boolean {
  return response.confidence >= threshold && response.status === 'success';
}

/**
 * Convert a confidence number to a human-readable label.
 */
export function confidenceToLabel(confidence: number): 'low' | 'medium' | 'high' | 'very-high' {
  if (confidence >= QUALITY_THRESHOLDS.veryHigh) return 'very-high';
  if (confidence >= QUALITY_THRESHOLDS.high) return 'high';
  if (confidence >= QUALITY_THRESHOLDS.medium) return 'medium';
  return 'low';
}

/**
 * Compute individual quality factors for a response.
 */
export function qualityFactors(response: AIResponse): {
  readonly relevance: number;
  readonly completeness: number;
  readonly accuracy: number;
  readonly coherence: number;
} {
  const contentStr = contentToString(response.content);
  const contentLength = contentStr.length;

  // Relevance: confidence is our best proxy
  const relevance = response.confidence;

  // Completeness: non-empty content with reasonable length
  let completeness = 0;
  if (contentLength > 0) completeness = 0.5;
  if (contentLength > 50) completeness = 0.7;
  if (contentLength > 200) completeness = 0.85;
  if (contentLength > 500) completeness = 1.0;
  if (response.status === 'partial') completeness *= 0.6;

  // Accuracy: derived from confidence and success status
  let accuracy = response.confidence;
  if (response.status !== 'success') accuracy *= 0.5;

  // Coherence: presence of structure indicates coherent output
  let coherence = 0.5;
  if (response.content.type === 'structured' || response.content.type === 'analysis') coherence = 0.9;
  if (response.content.type === 'code') coherence = 0.8;
  if (contentStr.includes('\n')) coherence = Math.min(1, coherence + 0.1);

  return { relevance, completeness, accuracy, coherence };
}

// ═══ §5 RESPONSE TRANSFORMATION — map, combine, format ═══

/**
 * Transform response content using a mapping function.
 */
export function transformResponse(
  response: AIResponse,
  fn: (content: AIContent) => AIContent,
): AIResponse {
  return { ...response, content: fn(response.content) };
}

/**
 * Combine multiple responses into a single response.
 * Uses the last model info, sums performance, averages confidence.
 */
export function combineResponses(responses: readonly AIResponse[]): AIResponse | null {
  if (responses.length === 0) return null;
  if (responses.length === 1) return responses[0] ?? null;

  const first = responses[0]!;
  const last = responses[responses.length - 1]!;

  const combinedValue = responses.map((r) => contentToString(r.content)).join('\n\n');
  const avgConfidence = responses.reduce((sum, r) => sum + r.confidence, 0) / responses.length;

  const totalPerf: AIPerformanceInfo = {
    totalTimeMs: responses.reduce((sum, r) => sum + r.performance.totalTimeMs, 0),
    inferenceTimeMs: responses.reduce((sum, r) => sum + r.performance.inferenceTimeMs, 0),
    tokensProcessed: responses.reduce((sum, r) => sum + r.performance.tokensProcessed, 0),
    tokensGenerated: responses.reduce((sum, r) => sum + r.performance.tokensGenerated, 0),
    cached: responses.some((r) => r.performance.cached),
  };

  const hasError = responses.some((r) => r.status === 'error');
  const allSuccess = responses.every((r) => r.status === 'success');
  const status: AIResponseStatus = hasError ? 'error' : allSuccess ? 'success' : 'partial';

  return {
    id: asAIResponseId(generateId('aires')),
    requestId: first.requestId,
    status,
    content: { type: 'text', value: combinedValue },
    model: last.model,
    performance: totalPerf,
    confidence: avgConfidence,
  };
}

/**
 * Truncate response content to a maximum length.
 */
export function truncateResponse(response: AIResponse, maxLength: number): AIResponse {
  const str = contentToString(response.content);
  if (str.length <= maxLength) return response;
  const truncated = str.slice(0, maxLength) + '...';
  return { ...response, content: { type: response.content.type, value: truncated } };
}

/**
 * Format a response for human-readable display.
 */
export function formatResponseForDisplay(response: AIResponse): string {
  const parts: string[] = [];
  parts.push(`[${response.status.toUpperCase()}] (${confidenceToLabel(response.confidence)} confidence)`);
  parts.push(`Model: ${response.model.provider}/${response.model.name} v${response.model.version}`);
  parts.push(`Time: ${response.performance.totalTimeMs}ms | Tokens: ${response.performance.tokensGenerated}`);
  parts.push('');
  parts.push(contentToString(response.content));
  return parts.join('\n');
}

/**
 * Convert a response to a markdown string.
 */
export function responseToMarkdown(response: AIResponse): string {
  const parts: string[] = [];
  parts.push(`## AI Response`);
  parts.push('');
  parts.push(`**Status:** ${response.status}`);
  parts.push(`**Confidence:** ${(response.confidence * 100).toFixed(1)}%`);
  parts.push(`**Model:** ${response.model.provider}/${response.model.name}`);
  parts.push('');

  if (response.content.type === 'code') {
    parts.push('```');
    parts.push(contentToString(response.content));
    parts.push('```');
  } else {
    parts.push(contentToString(response.content));
  }

  parts.push('');
  parts.push(`---`);
  parts.push(`*${response.performance.totalTimeMs}ms | ${response.performance.tokensGenerated} tokens*`);
  return parts.join('\n');
}

/**
 * Convert a response to a serializable JSON-safe object.
 */
export function responseToJSON(response: AIResponse): Record<string, unknown> {
  return {
    id: response.id,
    requestId: response.requestId,
    status: response.status,
    content: {
      type: response.content.type,
      value: response.content.value,
    },
    model: { ...response.model },
    performance: { ...response.performance },
    confidence: response.confidence,
    metadata: response.metadata,
  };
}

// ═══ §6 PERFORMANCE ANALYSIS — latency, throughput, cost ═══

/**
 * Analyze performance metrics and return an assessment.
 */
export function analyzePerformance(performance: AIPerformanceInfo): {
  readonly rating: 'fast' | 'acceptable' | 'slow' | 'timeout';
  readonly tokensPerSec: number;
  readonly overhead: number;
} {
  const tps = tokensPerSecond(performance);
  const overhead = performance.totalTimeMs - performance.inferenceTimeMs;

  let rating: 'fast' | 'acceptable' | 'slow' | 'timeout';
  if (performance.totalTimeMs < 500) rating = 'fast';
  else if (performance.totalTimeMs < 2000) rating = 'acceptable';
  else if (performance.totalTimeMs < 10000) rating = 'slow';
  else rating = 'timeout';

  return { rating, tokensPerSec: tps, overhead };
}

/**
 * Check if performance is within the given latency budget.
 */
export function isWithinLatencyBudget(performance: AIPerformanceInfo, budgetMs: number): boolean {
  return performance.totalTimeMs <= budgetMs;
}

/**
 * Compute tokens per second.
 */
export function tokensPerSecond(performance: AIPerformanceInfo): number {
  if (performance.inferenceTimeMs <= 0) return 0;
  return (performance.tokensGenerated / performance.inferenceTimeMs) * 1000;
}

/**
 * Estimate cost for a single performance record.
 */
export function costEstimate(performance: AIPerformanceInfo, costPerMToken: number): number {
  const totalTokens = performance.tokensProcessed + performance.tokensGenerated;
  return (totalTokens / 1_000_000) * costPerMToken;
}

/**
 * Compare two performance records: negative if a is better than b.
 */
export function comparePerformance(a: AIPerformanceInfo, b: AIPerformanceInfo): number {
  // Lower latency is better
  const latencyDiff = a.totalTimeMs - b.totalTimeMs;
  // Higher throughput is better
  const tpsDiff = tokensPerSecond(b) - tokensPerSecond(a);
  // Weight latency more
  return latencyDiff * 0.6 + tpsDiff * 0.4;
}

/**
 * Average multiple performance records.
 */
export function averagePerformance(performances: readonly AIPerformanceInfo[]): AIPerformanceInfo {
  if (performances.length === 0) {
    return { totalTimeMs: 0, inferenceTimeMs: 0, tokensProcessed: 0, tokensGenerated: 0, cached: false };
  }
  const count = performances.length;
  return {
    totalTimeMs: performances.reduce((s, p) => s + p.totalTimeMs, 0) / count,
    inferenceTimeMs: performances.reduce((s, p) => s + p.inferenceTimeMs, 0) / count,
    tokensProcessed: Math.round(performances.reduce((s, p) => s + p.tokensProcessed, 0) / count),
    tokensGenerated: Math.round(performances.reduce((s, p) => s + p.tokensGenerated, 0) / count),
    cached: performances.some((p) => p.cached),
  };
}

/**
 * Determine if performance is trending up, down, or stable over a history.
 */
export function performanceTrend(history: readonly AIPerformanceInfo[]): 'improving' | 'declining' | 'stable' {
  if (history.length < 3) return 'stable';

  const recentCount = Math.min(5, Math.floor(history.length / 2));
  const olderSlice = history.slice(0, recentCount);
  const newerSlice = history.slice(history.length - recentCount);

  const olderAvgLatency = olderSlice.reduce((s, p) => s + p.totalTimeMs, 0) / olderSlice.length;
  const newerAvgLatency = newerSlice.reduce((s, p) => s + p.totalTimeMs, 0) / newerSlice.length;

  const ratio = newerAvgLatency / (olderAvgLatency || 1);
  if (ratio < 0.85) return 'improving';
  if (ratio > 1.15) return 'declining';
  return 'stable';
}
